import type {
  AuditLogQueryParams,
  AuditLogResponse,
  AuditExportParams,
} from "../types/auditLog.type";
import type { IAuditLogRepository } from "../repositories/AuditLogRepository";
import { getErrorMessage } from "../utils/errorMessage";
export interface IAuditLogUseCase {
  getAuditLogs(params?: AuditLogQueryParams): Promise<AuditLogResponse>;
  exportAuditLogs(params: AuditExportParams): Promise<Blob>;
}
export class AuditLogUseCase implements IAuditLogUseCase {
  constructor(private readonly auditLogRepository: IAuditLogRepository) {}
  /**
   * Fetch audit logs with validation and error handling
   * @param params - Query parameters for filtering and pagination
   * @returns Paginated audit log response
   */
  async getAuditLogs(params?: AuditLogQueryParams): Promise<AuditLogResponse> {
    try {
      // Validate pagination parameters
      if (params?.page && params.page < 1) {
        throw new Error("Page number must be greater than 0");
      }
      if (params?.limit && (params.limit < 1 || params.limit > 100)) {
        throw new Error("Limit must be between 1 and 100");
      }
      // Validate date range if provided
      if (params?.dateFrom && params?.dateTo) {
        const from = new Date(params.dateFrom);
        const to = new Date(params.dateTo);
        if (from > to) {
          throw new Error("dateFrom must be before dateTo");
        }
      }
      // Call repository to fetch audit logs
      const response = await this.auditLogRepository.getAuditLogs(params);
      return response;
    } catch (error: unknown) {
      throw new Error(
        getErrorMessage(
          error,
          "Failed to fetch audit logs. Please try again later.",
        ),
      );
    }
  }
  /**
   * Export audit logs as a downloadable file
   * @param params - Export parameters (format, columns, filters)
   * @returns Blob of the exported file
   */
  async exportAuditLogs(params: AuditExportParams): Promise<Blob> {
    return this.auditLogRepository.exportAuditLogs(params);
  }
}
// Factory function to create use case with dependencies
export const createAuditLogUseCase = (
  auditLogRepository: IAuditLogRepository,
): AuditLogUseCase => {
  return new AuditLogUseCase(auditLogRepository);
};
